import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'primedocumentsign.firebaseapp.com';
const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;
const appId = import.meta.env.VITE_FIREBASE_APP_ID;
const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;

if (!apiKey || !projectId || !appId) {
  throw new Error(
    'Firebase config missing. Add VITE_FIREBASE_API_KEY, VITE_FIREBASE_PROJECT_ID, and VITE_FIREBASE_APP_ID to your .env file. Get these from Firebase Console → Project settings.'
  );
}

const firebaseConfig = {
  apiKey,
  authDomain,
  projectId,
  storageBucket,
  messagingSenderId,
  appId,
  measurementId: measurementId || undefined,
};

export const app = initializeApp(firebaseConfig);

/**
 * GA4 page views and conversion events use the existing gtag.js snippet
 * (`src/lib/ga4.js`) with this same measurement ID. Do not also boot
 * firebase/analytics here — that would duplicate page_view on G-22H7G0F0Q9.
 */

export const auth = getAuth(app);
// Memory only. IndexedDB persistence crashes crawlers and browsers that still
// have a cache written by a different Firebase project.
export const db = initializeFirestore(app, {
  localCache: memoryLocalCache(),
});
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

/**
 * Functions emulator is opt-in only. Default on localhost is production Cloud Functions (same project
 * as VITE_FIREBASE_*), so callable seeds work without `firebase emulators:start`.
 *
 * To hit the emulator instead: set `VITE_USE_FUNCTIONS_EMULATOR=true` and run
 * `firebase emulators:start --only functions` (port from firebase.json or VITE_FUNCTIONS_EMULATOR_PORT).
 */
const FUNCTIONS_EMULATOR_PORT = Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT || 5001);
if (typeof window !== 'undefined' && import.meta.env.DEV && import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true') {
  try {
    connectFunctionsEmulator(functions, '127.0.0.1', FUNCTIONS_EMULATOR_PORT);
  } catch {
    /* already connected (e.g. HMR) */
  }
}

/** GCP project id (for admin links to Cloud Console). */
export const firebaseProjectId = projectId;
